import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  CreditCard, 
  Truck, 
  MapPin, 
  AlertTriangle 
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface PostBookingInfoGraphicProps {
  onClose?: () => void;
  className?: string;
}

const steps = [
  {
    id: 1,
    title: 'Quote Generation',
    description: 'A quote will be generated based on the information provided. You will be notified when ready.',
    icon: FileText,
    status: 'pending'
  },
  {
    id: 2,
    title: 'Payment Confirmation',
    description: 'Follow the payment link using your quote number as reference to confirm your booking.',
    icon: CreditCard,
    status: 'pending'
  },
  {
    id: 3,
    title: 'Waybill Upload',
    description: 'A waybill will be uploaded to your profile confirming your booking details.',
    icon: Truck,
    status: 'pending'
  },
  {
    id: 4,
    title: 'Tracking Updates',
    description: 'Regular tracking updates will be provided during transit until delivery.',
    icon: MapPin,
    status: 'pending'
  }
];

const PostBookingInfoGraphic: React.FC<PostBookingInfoGraphicProps> = ({ 
  onClose, 
  className 
}) => {
  return (
    <div className={cn("w-full max-w-4xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6", className)}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
          What Happens Next?
        </h2>
        <p className="text-muted-foreground text-sm sm:text-base px-2">
          Your booking has been successfully submitted. Here's what to expect:
        </p>
      </div>

      {/* Timeline - Mobile: Vertical, Desktop: Horizontal */}
      <div className="space-y-4 md:space-y-0">
        {/* Mobile Layout */}
        <div className="lg:hidden space-y-3 sm:space-y-4">
          {steps.map((step, index) => (
            <TimelineStep 
              key={step.id}
              step={step}
              isLast={index === steps.length - 1}
              isMobile={true}
            />
          ))}
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="flex items-start justify-between relative px-4">
            {/* Progress Line */}
            <div className="absolute top-12 left-8 right-8 h-0.5 bg-muted z-0" />
            
            {steps.map((step, index) => (
              <div key={step.id} className="flex-1 relative z-10 first:pl-0 last:pr-0">
                <TimelineStep 
                  step={step}
                  isLast={index === steps.length - 1}
                  isMobile={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="border-warning/20 bg-warning/5 mx-2 sm:mx-0">
        <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
        <AlertDescription className="text-xs sm:text-sm leading-relaxed">
          <strong>Important:</strong> All parcels are reweighed after collection. 
          A payment request will be sent for parcels that weigh more than the quoted amount. 
          This process may cause transit delays.
        </AlertDescription>
      </Alert>

      {/* Action Button */}
      {onClose && (
        <div className="flex justify-center pt-2 sm:pt-4">
          <Button 
            onClick={onClose}
            className="px-6 sm:px-8 py-2 bg-primary hover:bg-primary/90 text-primary-foreground min-h-[44px] text-sm sm:text-base"
          >
            Got it!
          </Button>
        </div>
      )}
    </div>
  );
};

interface TimelineStepProps {
  step: typeof steps[0];
  isLast: boolean;
  isMobile: boolean;
}

const TimelineStep: React.FC<TimelineStepProps> = ({ step, isLast, isMobile }) => {
  const Icon = step.icon;
  
  return (
    <div className={cn(
      "relative",
      isMobile ? "flex gap-3 sm:gap-4" : "text-center"
    )}>
      {/* Mobile Connector Line */}
      {isMobile && !isLast && (
        <div className="absolute left-5 sm:left-6 top-11 sm:top-12 w-0.5 h-12 sm:h-16 bg-muted" />
      )}
      
      {/* Icon Circle */}
      <div className={cn(
        "rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center z-10",
        isMobile ? "w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0" : "w-16 h-16 mx-auto mb-4 bg-card"
      )}>
        <Icon className={cn(
          "text-primary",
          isMobile ? "w-4 h-4 sm:w-5 sm:h-5" : "w-6 h-6"
        )} />
      </div>
      
      {/* Content */}
      <div className={cn(
        "space-y-1 sm:space-y-2",
        isMobile ? "flex-1 pt-0.5 sm:pt-1" : ""
      )}>
        <div className={cn(
          "space-y-1",
          !isMobile && "max-w-48 mx-auto"
        )}>
          <h3 className={cn(
            "font-semibold text-foreground leading-tight",
            isMobile ? "text-sm sm:text-base" : "text-lg"
          )}>
            {step.title}
          </h3>
          <p className={cn(
            "text-muted-foreground leading-relaxed",
            isMobile ? "text-xs sm:text-sm" : "text-sm"
          )}>
            {step.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PostBookingInfoGraphic;